import { Injectable } from '@angular/core';
import { IAssignmentModuleCall } from 'src/app/interfaces/iassignment-module-call';
import { ICallFlag } from 'src/app/interfaces/icall-flag';
import { ITestSessionContext } from 'src/app/interfaces/self-serve/itest-session-context';

const TEST_SESSION_STORAGE_KEY = 'testSimulationCallUuid';

@Injectable({
  providedIn: 'root',
})
export class CallFlagService {
  saveCallFlags(calls: IAssignmentModuleCall[], scoreCriteria?: number) {
    let existingFlags: ICallFlag[] = [];
    try {
      // Safely parse existing data from localStorage
      const storedData = localStorage.getItem('callFlags');
      existingFlags = storedData ? JSON.parse(storedData) : [];

      // Ensure it's an array (in case of unexpected data)
      if (!Array.isArray(existingFlags)) {
        console.warn(
          'Invalid data found in localStorage. Resetting callFlags.'
        );
        existingFlags = [];
      }
    } catch (error) {
      console.error('Error parsing callFlags from localStorage:', error);
      existingFlags = []; // Reset to an empty array in case of JSON parse error
    }

    // Create a map of existing flags for quick lookup
    const flagsMap = new Map<string, ICallFlag>(
      existingFlags.map((call) => [call.callUuid, call])
    );

    // Update existing flags and add new ones
    calls.forEach((call) => {
      const isMaxAttemptsReached = this.isMaxAttemptsReached(call);
      flagsMap.set(call.callUuid, {
        callUuid: call.callUuid,
        tavusEnabled: call.tavusEnabled,
        visualAidEnabled: call.visualAidEnabled,
        isMaxAttemptsReached,
        pauseEnabled: call.pauseEnabled,
        callThumbnail: call.callThumbnail ?? '',
        language: call.language ?? null,
      });
    });

    // Convert back to an array and save to localStorage
    localStorage.setItem(
      'callFlags',
      JSON.stringify(Array.from(flagsMap.values()))
    );
  }

  isMaxAttemptsReached(call: IAssignmentModuleCall): boolean {
    const maxAttempts = call.maxNumberOfAttempts ?? 0;
    const attempts = call.numberOfPreviousAttempts ?? 0;
    return maxAttempts > 0 && attempts >= maxAttempts;
  }

  isIncompleteMessageVisible(call: IAssignmentModuleCall, scoreCriteria?: number): boolean {
    const maxAttempts = call.maxNumberOfAttempts ?? 0;
    const attempts = call.numberOfPreviousAttempts ?? 0;
    const hasReachedMaxAttempts = maxAttempts > 0 && attempts >= maxAttempts;

    // If max attempts not reached, return false
    if (!hasReachedMaxAttempts) {
      return false;
    }

    // If score criteria is provided and call has a score, only show message if criteria not met
    if (scoreCriteria !== undefined && call.callScore !== null) {
      return call.callScore < scoreCriteria;
    }

    // If call is COMPLETE or CERTIFIED, don't show message
    if (call.callStatus === 'COMPLETE' || call.callStatus === 'CERTIFIED') {
      return false;
    }

    return true;
  }

  getCallFlagsLS(callUuid: string) {
    // Get call flags from localStorage
    const callFlagsFromLocalStorage = localStorage.getItem('callFlags');
    if (callFlagsFromLocalStorage) {
      let storedFlags;
      try {
        storedFlags = JSON.parse(callFlagsFromLocalStorage) || [];

        // Check if the storedFlags is empty or callUuid is not found
        if (storedFlags.length === 0) {
          console.log('No flags stored in localStorage.');
          return null;
        }

        const call = storedFlags.find(
          (call: ICallFlag) => call.callUuid === callUuid
        );

        if (!call) {
          console.error(`No flags found in /call for callUuid: ${callUuid}`);
          return null;
        }

        return call;
      } catch (e) {
        console.error('Invalid JSON in callFlags:', callFlagsFromLocalStorage);
      }
    }
    return null;
  }

  getIsMaxAttemptsReached(callUuid: string): boolean {
    const callFlags = this.getCallFlagsLS(callUuid);
    return callFlags ? callFlags.isMaxAttemptsReached : false;
  }

  markTestSession(context: ITestSessionContext): void {
    localStorage.setItem(TEST_SESSION_STORAGE_KEY, JSON.stringify(context));
  }

  consumeTestSession(callUuid: string): ITestSessionContext | null {
    const stored = localStorage.getItem(TEST_SESSION_STORAGE_KEY);
    if (!stored) return null;

    let context: ITestSessionContext;
    try {
      context = JSON.parse(stored);
    } catch (e) {
      console.error('Invalid JSON in testSimulationCallUuid:', stored);
      return null;
    }

    if (context.callUuid !== callUuid) return null;

    localStorage.removeItem(TEST_SESSION_STORAGE_KEY);
    return context;
  }
}
